import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ITakeoffElement } from '@interfaces/ICadTakeoff';
import { SearchInput } from '@components/ui/SearchInput/SearchInput';
import { chosenQuantity, floorBands, tagParts, withGroupCount, type ClaimUnitKey, type IElementGroup } from '../../stock/claimElements';
import { floorsLabel } from '../../stock/takeoffByFloor';
import { PickerBand, PickerSections, type ISectionContext } from './StockElementPickerBands';
import { BackToMaterialsButton } from './BackToMaterialsButton';
import styles from './StockElementPicker.module.css';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';

interface IStockElementPickerProps {
  kind: string;
  materialName: string;
  /** The task's floors in order, with their names — empty when the task has
   *  none: the header then says "the model". Several floors band the list
   *  by floor. */
  floorIds: string[];
  floorNames: string[];
  /** Which floor an element sits on (`useTaskStock.floorOf`). */
  floorOf: (globalId: string) => string | null;
  /** The type's elements on the task's floors, grouped across storeys. */
  elements: ITakeoffElement[];
  /** Elements other tasks hold, globalId → task name. */
  taken: ReadonlyMap<string, string>;
  /** The claim's current selection for this type. */
  initialIds: string[];
  unitKey: ClaimUnitKey | null;
  unitLabel: string;
  onConfirm: (ids: string[]) => void;
  onCancel: () => void;
}

/** Case-insensitive substring match on the Revit id and the GlobalId. */
function matches(el: ITakeoffElement, query: string): boolean {
  return el.globalId.toLowerCase().includes(query) || (el.tag !== null && el.tag.toLowerCase().includes(query));
}

/** `set` with `key` flipped in or out — never a mutation. */
function toggled(set: ReadonlySet<string>, key: string): Set<string> {
  const next = new Set(set);
  if (next.has(key)) next.delete(key);
  else next.add(key);
  return next;
}

/**
 * The element list: takes the Stock tab body's place while the user picks
 * which elements of one type the task covers. Local state until *Confirm
 * selection* hands the ids back; *Cancel* / *← Back to materials* return
 * with nothing changed. The drawer's own footer is hidden for as long as this
 * is mounted (the tab lifts that via `onPicking`), so the bottom bar here is
 * the drawer's bottom bar.
 *
 * The selection starts from the claim's stored ids MINUS anything the list
 * cannot offer: an element another task now holds, or one the CAD no longer
 * has. Confirming therefore resolves a conflict by itself — which is what the
 * outcome's "Select again" asks for.
 *
 * A task on several floors gets one folding band per floor holding elements
 * of the kind, top floor first, each with its own type sections; on one
 * floor the list looks as it always did, with no band header at all.
 */
export function StockElementPicker({
  kind,
  materialName,
  floorIds,
  floorNames,
  floorOf,
  elements,
  taken,
  initialIds,
  unitKey,
  unitLabel,
  onConfirm,
  onCancel,
}: IStockElementPickerProps) {
  const { t } = useTranslation();
  const [chosen, setChosen] = useState<ReadonlySet<string>>(() => {
    const offered = new Set(elements.filter((el) => !taken.has(el.globalId)).map((el) => el.globalId));
    return new Set(initialIds.filter((id) => offered.has(id)));
  });
  const [filter, setFilter] = useState('');
  const [open, setOpen] = useState<ReadonlySet<string>>(new Set());
  // Sections start open; this holds the ones the user folded.
  const [closedSections, setClosedSections] = useState<ReadonlySet<string>>(new Set());

  // One band per floor holding elements of the kind, over EVERY element — a
  // floor's count must not change with the filter. Headers only when there
  // are several: a single floor's band is the plain list.
  const bands = useMemo(() => floorBands(elements, floorIds, floorNames, floorOf), [elements, floorIds, floorNames, floorOf]);
  const banded = bands.length > 1;
  // The first band with a free element starts open (the first band when none
  // has one), the rest closed; this holds the closed ones.
  const [closedBands, setClosedBands] = useState<ReadonlySet<string>>(() => {
    const first = Math.max(0, bands.findIndex((b) => b.elements.some((el) => !taken.has(el.globalId))));
    return new Set(bands.filter((_, i) => i !== first).map((b) => b.floorId));
  });

  const takenCount = useMemo(() => elements.filter((el) => taken.has(el.globalId)).length, [elements, taken]);
  const query = filter.trim().toLowerCase();
  const visible = useMemo(
    () => (query === '' ? elements : elements.filter((el) => matches(el, query))),
    [elements, query],
  );
  const visibleIds = useMemo(() => new Set(visible.map((el) => el.globalId)), [visible]);
  // Over EVERY element, not the filtered view: a piece's number must not
  // change because its siblings were filtered out.
  const parts = useMemo(() => tagParts(elements), [elements]);
  // Headers only when the type tells rows apart — judged on the whole list,
  // so a filter that leaves one type in view does not make its header vanish.
  const collapsible = useMemo(() => new Set(elements.map((el) => el.typeName)).size > 1, [elements]);
  const total = chosenQuantity(elements, chosen, unitKey);

  /** A new selection with `ids` added or removed — never a mutation. */
  function update(ids: Iterable<string>, add: boolean) {
    setChosen((prev) => {
      const next = new Set(prev);
      for (const id of ids) {
        if (add) next.add(id);
        else next.delete(id);
      }
      return next;
    });
  }

  function toggle(id: string) {
    update([id], !chosen.has(id));
  }

  /** Every free member on, unless they all already are — then all off. */
  function toggleGroup(group: IElementGroup) {
    const free = group.members.filter((m) => m.taken === null);
    update(free.map((m) => m.el.globalId), !free.every((m) => m.chosen));
  }

  function setGroupCount(group: IElementGroup, count: number) {
    setChosen((prev) => withGroupCount(prev, group, count));
  }

  const ctx: ISectionContext = {
    kind,
    unitLabel,
    parts,
    collapsible,
    taken,
    chosen,
    unitKey,
    closedSections,
    onToggleSection: (key) => setClosedSections((prev) => toggled(prev, key)),
    open,
    onToggleOpen: (key) => setOpen((prev) => toggled(prev, key)),
    onToggle: toggle,
    onToggleGroup: toggleGroup,
    onCount: setGroupCount,
  };

  // "Select all" (the free ones) and "Clear" act on what the filter shows: with no
  // filter that is every element, with one only the rows in view.
  const visibleFree = visible.filter((el) => !taken.has(el.globalId)).map((el) => el.globalId);

  return (
    <div className={styles.picker}>
      <BackToMaterialsButton className={styles.back} onClick={onCancel} />
      <header className={styles.head}>
        <b>
          {t('scheduler.panel.stock.pickerTitle', {
            kind,
            floor: floorsLabel(floorNames, t),
            material: materialName,
          })}
        </b>
        <span>
          {banded
            ? t('scheduler.panel.stock.pickerSummaryFloors', { count: elements.length, floors: bands.length, taken: takenCount })
            : t('scheduler.panel.stock.pickerSummary', { count: elements.length, taken: takenCount })}
        </span>
      </header>
      <div className={styles.filter}>
        <SearchInput
          value={filter}
          onChange={setFilter}
          placeholder={t('scheduler.panel.stock.filterById')}
          ariaLabelClear={t('scheduler.panel.stock.clearFilter')}
        />
        <button type="button" className={styles.tool} onClick={() => update(visibleFree, true)}>
          {t('scheduler.panel.stock.selectAllFree')}
        </button>
        <button type="button" className={styles.tool} onClick={() => update(visible.map((el) => el.globalId), false)}>
          {t('scheduler.panel.stock.clear')}
        </button>
      </div>
      <ScrollArea className={styles.rows}>
        {banded ? (
          bands.map((band) => (
            <PickerBand
              key={band.floorId}
              band={band}
              shown={band.elements.filter((el) => visibleIds.has(el.globalId))}
              open={!closedBands.has(band.floorId)}
              onToggle={() => setClosedBands((prev) => toggled(prev, band.floorId))}
              ctx={ctx}
            />
          ))
        ) : (
          <PickerSections scope="" elements={visible} ctx={ctx} />
        )}
      </ScrollArea>
      <div className={styles.summary}>
        <span>{t('scheduler.panel.stock.chosenCount', { count: chosen.size })}</span>
        <b>{`${total} ${unitLabel}`}</b>
      </div>
      <div className={styles.footer}>
        <button type="button" className={styles.cancel} onClick={onCancel}>
          {t('scheduler.panel.stock.cancel')}
        </button>
        <button
          type="button"
          className={styles.confirm}
          // Never disabled: confirming an EMPTY selection is how a type's
          // elements are dropped from the claim again. Handed back in the
          // model's own order, not click order, so a stored claim reads the
          // same however it was picked.
          onClick={() => onConfirm(elements.filter((el) => chosen.has(el.globalId)).map((el) => el.globalId))}
        >
          {chosen.size === 0
            ? t('scheduler.panel.stock.confirmSelection')
            : t('scheduler.panel.stock.confirmSelectionCount', { count: chosen.size })}
        </button>
      </div>
    </div>
  );
}
